// components/DeliverySession/CalculatedRouteMap/index.tsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { LocationPoint } from '../../../../../../redux/cartSlice';
import { useGoogleMaps } from '../../../../../../hooks/useGoogleMaps';
import { Spinner } from 'react-bootstrap';
import '../RouteMap/index.css';
import './index.css';

interface CalculatedRouteMapProps {
  hubLocation: LocationPoint;
  orderLocations: LocationPoint[]; // just the order stops
  accentColor: string;
  label: string;
  distance: number;
  subtitle?: string;
  height?: number;
}

const toGoogleLatLng = (point: LocationPoint) => ({
  lat: point.lat,
  lng: point.lng
});

const hasValidCoordinates = (point: LocationPoint): boolean => {
  return point.lat !== 0 || point.lng !== 0;
};

interface InfoWindowData {
  position: google.maps.LatLng;
  title: string;
  coordinates: string;
  type: 'hub' | 'stop';
  stopNumber?: number;
}

const CalculatedRouteMap: React.FC<CalculatedRouteMapProps> = ({
  hubLocation,
  orderLocations,
  accentColor,
  label,
  distance,
  subtitle,
  height = 500,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer | null>(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const { isLoaded, loadError } = useGoogleMaps();

  const validHub = useMemo(() => hasValidCoordinates(hubLocation) ? hubLocation : null, [hubLocation]);
  const validOrderLocations = useMemo(() => orderLocations.filter(hasValidCoordinates), [orderLocations]);

  // Full route: hub -> stop1 -> stop2 -> ... -> hub
  const allPoints = useMemo(() => {
    if (!validHub) return [];
    return [validHub, ...validOrderLocations, validHub];
  }, [validHub, validOrderLocations]);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || map) return;

    const defaultCenter = validHub ? toGoogleLatLng(validHub) : { lat: 6.9271, lng: 79.8612 };

    const newMap = new google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: 13,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
      ],
    });
    setMap(newMap);

    const renderer = new google.maps.DirectionsRenderer({
      map: newMap,
      suppressMarkers: true,
      preserveViewport: false,
      polylineOptions: {
        strokeColor: accentColor,
        strokeWeight: 5,
        strokeOpacity: 0.8,
      },
    });
    setDirectionsRenderer(renderer);

    const infoWin = new google.maps.InfoWindow({
      maxWidth: 250,
      pixelOffset: new google.maps.Size(0, -10),
    });
    setInfoWindow(infoWin);

    setHasInitialized(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, mapRef.current]);

  useEffect(() => {
    if (directionsRenderer) {
      directionsRenderer.setOptions({
        polylineOptions: {
          strokeColor: accentColor,
          strokeWeight: 5,
          strokeOpacity: 0.8,
        },
      });
    }
  }, [accentColor, directionsRenderer]);

  const createInfoWindowContent = (data: InfoWindowData): string => {
    const isHub = data.type === 'hub';
    const color = isHub ? '#f59e0b' : accentColor;
    const icon = isHub ? '🏠' : '📍';
    const typeLabel = isHub ? 'HUB' : `STOP ${data.stopNumber}`;

    return `
      <div class="delivery-route-map__info-window">
        <div class="delivery-route-map__info-header" style="border-bottom-color: ${color}">
          <span class="delivery-route-map__info-icon">${icon}</span>
          <span class="delivery-route-map__info-title">${data.title}</span>
        </div>
        <div class="delivery-route-map__info-content">
          <div class="delivery-route-map__info-type" style="color: ${color}">${typeLabel}</div>
          <div class="delivery-route-map__info-coordinates">
            <div class="delivery-route-map__info-coord-row">
              <span class="delivery-route-map__info-coord-label">Latitude:</span>
              <span class="delivery-route-map__info-coord-value">${data.coordinates.split(', ')[0]}</span>
            </div>
            <div class="delivery-route-map__info-coord-row">
              <span class="delivery-route-map__info-coord-label">Longitude:</span>
              <span class="delivery-route-map__info-coord-value">${data.coordinates.split(', ')[1]}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  };

  const clearMarkers = () => {
    markers.forEach(m => m.setMap(null));
    setMarkers([]);
  };

  const addMarkers = (map: google.maps.Map) => {
    clearMarkers();
    const newMarkers: google.maps.Marker[] = [];

    // Hub marker (amber/yellow) - only once even though it appears twice in route
    if (validHub) {
      const hubPos = toGoogleLatLng(validHub);
      const hubMarker = new google.maps.Marker({
        position: hubPos,
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 13,
          fillColor: '#f59e0b',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3,
        },
        label: {
          text: 'H',
          color: '#ffffff',
          fontSize: '12px',
          fontWeight: 'bold',
        },
        title: 'Hub',
        animation: google.maps.Animation.DROP,
      });

      hubMarker.addListener('mouseover', () => {
        if (infoWindow) {
          infoWindow.setContent(createInfoWindowContent({
            position: new google.maps.LatLng(hubPos.lat, hubPos.lng),
            title: 'Hub Location',
            coordinates: `${validHub.lat}° N, ${validHub.lng}° E`,
            type: 'hub',
          }));
          infoWindow.open(map, hubMarker);
        }
      });
      hubMarker.addListener('mouseout', () => infoWindow?.close());
      newMarkers.push(hubMarker);
    }

    // Order stop markers
    validOrderLocations.forEach((point, index) => {
      const pos = toGoogleLatLng(point);
      const stopNum = index + 1;
      const stopMarker = new google.maps.Marker({
        position: pos,
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 11,
          fillColor: accentColor,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2.5,
        },
        label: {
          text: stopNum.toString(),
          color: '#ffffff',
          fontSize: '11px',
          fontWeight: 'bold',
        },
        title: `Stop ${stopNum}`,
        animation: google.maps.Animation.DROP,
      });

      stopMarker.addListener('mouseover', () => {
        if (infoWindow) {
          infoWindow.setContent(createInfoWindowContent({
            position: new google.maps.LatLng(pos.lat, pos.lng),
            title: `Stop ${stopNum}`,
            coordinates: `${point.lat}° N, ${point.lng}° E`,
            type: 'stop',
            stopNumber: stopNum,
          }));
          infoWindow.open(map, stopMarker);
        }
      });
      stopMarker.addListener('mouseout', () => infoWindow?.close());
      newMarkers.push(stopMarker);
    });

    setMarkers(newMarkers);
  };

  const calculateRoute = async () => {
    if (!map || !directionsRenderer || allPoints.length < 2) return;

    setIsLoading(true);
    setError(null);

    try {
      directionsRenderer.setDirections(null);
      clearMarkers();

      if (allPoints.length >= 2) {
        const directionsService = new google.maps.DirectionsService();

        // origin = hub, destination = hub, waypoints = order stops
        const waypoints = validOrderLocations.map(point => ({
          location: toGoogleLatLng(point),
          stopover: true,
        }));

        const request: google.maps.DirectionsRequest = {
          origin: toGoogleLatLng(validHub!),
          destination: toGoogleLatLng(validHub!),
          waypoints,
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: false,
          provideRouteAlternatives: false,
        };

        try {
          const result = await directionsService.route(request);
          directionsRenderer.setDirections(result);
        } catch (routeErr) {
          console.error('Route calculation failed:', routeErr);
          setError('Could not calculate driving route.');
        }
      }

      addMarkers(map);

      // Fit bounds to all unique points (hub + order stops)
      const uniquePoints = [validHub!, ...validOrderLocations];
      if (uniquePoints.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        uniquePoints.forEach(p => bounds.extend(toGoogleLatLng(p)));
        map.fitBounds(bounds);
        if (uniquePoints.length === 1) map.setZoom(14);
      }
    } catch (err) {
      console.error('Error displaying calculated route:', err);
      setError('Error displaying route.');
      if (map) {
        addMarkers(map);
        const bounds = new google.maps.LatLngBounds();
        [validHub!, ...validOrderLocations].forEach(p => bounds.extend(toGoogleLatLng(p)));
        map.fitBounds(bounds);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (map && directionsRenderer && hasInitialized && validHub) {
      calculateRoute();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, directionsRenderer, hasInitialized, allPoints]);

  useEffect(() => {
    return () => {
      clearMarkers();
      infoWindow?.close();
      directionsRenderer?.setMap(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!validHub) {
    return (
      <div className="delivery-route-map delivery-route-map--empty" style={{ height: `${height}px` }}>
        <span className="delivery-route-map__empty-text">No hub location available</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="delivery-route-map delivery-route-map--error" style={{ height: `${height}px` }}>
        <span className="delivery-route-map__error-text">Error loading Google Maps</span>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="delivery-route-map delivery-route-map--loading" style={{ height: `${height}px` }}>
        <Spinner animation="border" variant="primary" />
        <span className="delivery-route-map__loading-text">Loading map...</span>
      </div>
    );
  }

  return (
    <div className="delivery-route-map">
      {/* Header */}
      <div
        className="delivery-route-map__header"
        style={{ borderBottomColor: `${accentColor}20`, backgroundColor: `${accentColor}08` }}
      >
        <div className="delivery-route-map__header-left">
          <div className="delivery-route-map__status-dot" style={{ backgroundColor: accentColor }} />
          <div className="delivery-route-map__title-wrapper">
            <div className="delivery-route-map__label" style={{ color: accentColor }}>{label}</div>
            {subtitle && <div className="delivery-route-map__subtitle">{subtitle}</div>}
          </div>
        </div>
        <span className="delivery-route-map__distance-value">
          {distance > 0 ? `${distance} km` : `${validOrderLocations.length} stop${validOrderLocations.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Map canvas */}
      <div
        className="delivery-route-map__canvas"
        style={{ height: `${height - 100}px`, minHeight: `${height - 100}px` }}
      >
        <div ref={mapRef} className="delivery-route-map__google-map" />
        {isLoading && (
          <div className="delivery-route-map__loading-overlay">
            <Spinner animation="border" variant="primary" size="sm" />
            <span>Calculating route...</span>
          </div>
        )}
        {error && (
          <div className="delivery-route-map__error-overlay">
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Footer - EXACT VALUES (no rounding) */}
      <div className="delivery-route-map__footer">
        <div className="delivery-route-map__coordinate">
          <div className="delivery-route-map__coordinate-label">HUB (START &amp; END)</div>
          <div className="delivery-route-map__coordinate-value">
            {validHub.lat}° N, {validHub.lng}° E
          </div>
        </div>
        {validOrderLocations.length > 0 && (
          <div className="delivery-route-map__coordinate delivery-route-map__coordinate--right">
            <div className="delivery-route-map__coordinate-label">STOPS</div>
            <div className="delivery-route-map__coordinate-value">
              {validOrderLocations.length} order location{validOrderLocations.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalculatedRouteMap;